import { useState } from "react";
import { useSession } from "../session/useSession";
import { ui, disabledStyle } from "../components/uiStyles";

/**
 * Paso 1: Descubrimiento de categorías de producto.
 *
 * Tres modos visuales según el state de la sesión:
 *   1. Sin respuesta → formulario de query inicial
 *   2. status="need_info" → pregunta de clarificación + chips + input refinamiento
 *   3. status="ok" → lista de suggestions con checkboxes para confirmar una
 */
export function Step1Discovery() {
  const {
    formData,
    responses,
    loading,
    error,
    runDiscovery,
    confirmProduct,
  } = useSession();

  // Estado LOCAL del componente — lo que el usuario está tipeando aquí y ahora.
  // Inicializo desde formData por si reanudamos una sesión (estados pre-llenos).
  const [query, setQuery] = useState(formData.initialQuery || "");
  const [useContext, setUseContext] = useState(formData.useContext || "");
  const [refineAnswer, setRefineAnswer] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  // Lectura del state global
  const r1 = responses[1];
  const toolOutput = r1?.toolOutput;
  const needsInfo = toolOutput?.status === "need_info";
  const hasSuggestions =
    toolOutput?.status === "ok" && Array.isArray(toolOutput.suggestions);

  // Handlers
  const handleInitialSearch = () => {
    if (!query.trim()) return;
    runDiscovery(query, useContext);
  };
  const handleRefine = (refinement) => {
    runDiscovery(formData.initialQuery, formData.useContext, refinement);
    setRefineAnswer(""); // limpia el input
  };
  const handleConfirm = () => {
    const chosen = toolOutput.suggestions.find((s) => s.id === selectedId);
    if (chosen) confirmProduct(chosen.name);
  };

  return (
    <div style={ui.card}>
      <h2 style={ui.h2}>¿Qué estás buscando?</h2>

      {/* Estado 1: formulario inicial */}
      {!toolOutput && (
        <>
          <div style={{ marginBottom: "14px" }}>
            <label style={ui.label}>
              Describe lo que necesitas (puede ser una idea general o algo específico)
            </label>
            <input
              style={ui.input}
              type="text"
              placeholder="ej. una laptop para diseño, algo para hacer ejercicio en casa..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInitialSearch()}
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={ui.label}>
              Contexto adicional (opcional) — uso, espacio, experiencia, para quién es
            </label>
            <textarea
              style={ui.textarea}
              placeholder="ej. departamento pequeño, soy principiante, presupuesto ajustado..."
              value={useContext}
              onChange={(e) => setUseContext(e.target.value)}
            />
          </div>
          <button
            style={{
              ...ui.primaryBtn,
              ...(!query.trim() || loading ? disabledStyle : {}),
            }}
            disabled={!query.trim() || loading}
            onClick={handleInitialSearch}
          >
            {loading ? "Buscando ideas..." : "Buscar sugerencias →"}
          </button>
        </>
      )}

      {error && <div style={ui.errorBox}>❌ {error}</div>}

      {/* Estado 2: necesita más info */}
      {needsInfo && (
        <div style={{ marginTop: "16px" }}>
          <div style={ui.infoBadge}>
            <strong>{toolOutput.question}</strong>
          </div>

          {Array.isArray(toolOutput.hint_options) && toolOutput.hint_options.length > 0 && (
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
              {toolOutput.hint_options.map((opt, i) => (
                <button key={i} style={ui.chip} onClick={() => handleRefine(opt)}>
                  {opt}
                </button>
              ))}
            </div>
          )}

          <div style={{ marginTop: "14px" }}>
            <label style={ui.label}>O describe con tus palabras</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                style={ui.input}
                type="text"
                value={refineAnswer}
                onChange={(e) => setRefineAnswer(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && refineAnswer.trim() && handleRefine(refineAnswer)
                }
              />
              <button
                style={{
                  ...ui.primaryBtn,
                  ...(!refineAnswer.trim() || loading ? disabledStyle : {}),
                }}
                disabled={!refineAnswer.trim() || loading}
                onClick={() => handleRefine(refineAnswer)}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estado 3: hay sugerencias */}
      {hasSuggestions && (
        <div style={{ marginTop: "16px" }}>
          {toolOutput.intro && (
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "14px" }}>
              {toolOutput.intro}
            </p>
          )}

          {toolOutput.suggestions.map((s) => (
            <div
              key={s.id}
              style={ui.optionCard(selectedId === s.id)}
              onClick={() => setSelectedId(s.id)}
            >
              <div style={ui.checkbox(selectedId === s.id)}>
                {selectedId === s.id ? "✓" : ""}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: "14px", color: "#1a1a2e" }}>
                  {s.name}
                </div>
                <div style={{ fontSize: "13px", color: "#666", marginTop: "3px" }}>
                  {s.description}
                </div>
                {s.fit_reason && (
                  <div style={{ fontSize: "12px", color: "#6c63ff", marginTop: "4px" }}>
                    ✓ {s.fit_reason}
                  </div>
                )}
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: "10px", marginTop: "16px", flexWrap: "wrap" }}>
            <button
              style={{ ...ui.primaryBtn, ...(!selectedId ? disabledStyle : {}) }}
              disabled={!selectedId}
              onClick={handleConfirm}
            >
              Continuar con esta opción →
            </button>
          </div>
        </div>
      )}

      {/* Refinamiento disponible solo cuando hay sugerencias. En modo
          need_info ya tenemos otro input arriba, no duplicarlo. */}
      {hasSuggestions && (
        <div
          style={{
            marginTop: "20px",
            padding: "14px",
            background: "#faf9ff",
            borderRadius: "8px",
            border: "1px dashed #d8d4ff",
          }}
        >
          <label style={ui.label}>
            ¿No es lo que buscabas? Agrega contexto para refinar
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              style={ui.input}
              type="text"
              placeholder="ej. más enfocado en deportes, marca premium, etc."
              value={refineAnswer}
              onChange={(e) => setRefineAnswer(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" &&
                refineAnswer.trim() &&
                handleRefine(refineAnswer)
              }
            />
            <button
              style={{
                ...ui.primaryBtn,
                ...(!refineAnswer.trim() || loading ? disabledStyle : {}),
              }}
              disabled={!refineAnswer.trim() || loading}
              onClick={() => handleRefine(refineAnswer)}
            >
              Refinar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
